 import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { EditTicketAttachmentsProps } from "./EditableTicketChat/EditableTicketAttachment";
import { useUpdateEditableTicketChatContent } from "./EditableTicketChat/useUpdateEditableTicketChatContent";
import { platformService } from "../../services/platform.service";
import { uploadService } from "../../services/upload.service";
import { AsyncQueue } from "../../common/AsyncQueue";
import { attachmentLocation } from "../../utils/attachmentLocation";
import { useSetState } from "../../../AppState";
import type { FileProps } from "../EditTicketAttachments/EditTicketAttachments";
import { markdownToSlate, slateToMd } from "../Wysiwyg/slate-editor-helpers";

const api = new platformService();
const uploadHelper = new uploadService();

export const useTicketChatContent = (
  comment: any,
  usersInRole: PlatformUser[],
  ticketId: number,
  isEndUserPage: boolean,
  onCommentUpdated?: () => void
) => {
  const setAppState = useSetState();
  const { updateChatContent, loading: updateLoading } = useUpdateEditableTicketChatContent();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({});
  const [pendingAction, setPendingAction] = useState<"saving" | "sending">("saving");

  const [assignees, setAssignees] = useState<PlatformUser[]>([]);
  const [commentBody, setCommentBody] = useState<string>(comment.Body);
  const [commentIsPublic, setCommentIsPublic] = useState<boolean>(comment?.IsPublic ?? true);
  const [commentFileAttachments, setCommentFileAttachments] = useState<EditTicketAttachmentsProps[] | undefined>(comment?.FileAttachments);
  const prevCommentRef = useRef(comment);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isEditModeReady, setIsEditModeReady] = useState(false);
  const [message, setMessage] = useState<string>(comment.Body);
  const [editedAttachmentsList, setEditedAttachmentsList] = useState<EditTicketAttachmentsProps[]>(comment?.FileAttachments || []);
  const [isPublic, setIsPublic] = useState<boolean>(comment?.IsPublic ?? true);

  // Set assignees from current state
  useEffect(
    () => setAssignees(usersInRole),
    [usersInRole]
  );

  // Sync state when comment prop changes. When editing, refresh brings new data first, then we close edit state.
  useEffect(() => {
    const commentChanged =
      prevCommentRef.current?.Body !== comment?.Body ||
      prevCommentRef.current?.IsPublic !== comment?.IsPublic ||
      prevCommentRef.current?.FileAttachments?.length !== comment?.FileAttachments?.length ||
      (prevCommentRef.current?.FileAttachments && comment?.FileAttachments &&
       prevCommentRef.current.FileAttachments.some((att: any, idx: number) =>
         att.Id !== comment.FileAttachments[idx]?.Id
       ));

    if (commentChanged) {
      if (comment?.FileAttachments !== undefined) {
        setCommentFileAttachments(comment.FileAttachments);
      }
      if (comment?.Body !== undefined) {
        setCommentBody(comment.Body);
      }
      if (comment?.IsPublic !== undefined) {
        setCommentIsPublic(comment.IsPublic);
      }
      prevCommentRef.current = comment;
      if (isEditing) {
        setIsEditing(false);
      }
    }
  }, [comment?.FileAttachments, comment?.Body, comment?.IsPublic, isEditing]);

  const normalizedCommentBody = useMemo(() => {
    const cleaned = commentBody.replace(/&nbsp; \n/g, '\n');
    return slateToMd(markdownToSlate(cleaned));
  }, [commentBody]);
  
  useEffect(() => {
    if (isEditing) {
      setMessage(normalizedCommentBody);
      setEditedAttachmentsList(commentFileAttachments || []);
      setIsPublic(commentIsPublic);
      setIsEditModeReady(true);
    } else {
      setIsEditModeReady(false);
    }
  }, [isEditing]);

  // Check if attachments have been modified (files added or removed)
  const originalAttachmentIds = new Set<number>(
    (comment?.FileAttachments || []).map((att: EditTicketAttachmentsProps) => Number(att.Id))
  );
  const currentAttachmentIds = new Set<number>(
    (commentFileAttachments || []).map((att: EditTicketAttachmentsProps) => Number(att.Id))
  );

  const attachmentsModified =
    originalAttachmentIds.size !== currentAttachmentIds.size ||
    Array.from(originalAttachmentIds).some((id: number) => !currentAttachmentIds.has(id));

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setMessage(normalizedCommentBody);
    setEditedAttachmentsList(commentFileAttachments || []);
    setIsPublic(commentIsPublic);
  }, [normalizedCommentBody, commentFileAttachments, commentIsPublic]);

  const savedAttachments = commentFileAttachments || [];
  const hasChanges =
    message.trim() !== normalizedCommentBody.trim() ||
    isPublic !== commentIsPublic ||
    editedAttachmentsList.length !== savedAttachments.length ||
    editedAttachmentsList.some((att, idx) => att.Id !== savedAttachments[idx]?.Id);

  const performUpdate = useCallback(async (newFilesToUpload: File[], notify: boolean) => {
    setPendingAction(notify ? "sending" : "saving");
    const attachmentsOnServer = commentFileAttachments ?? comment?.FileAttachments ?? [];
    const editedAttachmentIds = new Set(editedAttachmentsList.map((att) => att.Id));
    const filesToDelete = attachmentsOnServer
      .filter((att: EditTicketAttachmentsProps) => !editedAttachmentIds.has(att.Id))
      .map((att: EditTicketAttachmentsProps) => att.Id);

    type FilesToAddItem = { FileName: string; MediaType: string; FileSize: number; DriveItemId: string; DriveItemGroupId?: string; DriveId?: string };
    let filesToAdd: FilesToAddItem[] | undefined;
    if (newFilesToUpload.length > 0) {
      setIsUploading(true);
      setUploadProgress(Object.fromEntries(newFilesToUpload.map((_, idx) => [idx, 0])));
      try {
        const queue = AsyncQueue(2);
        const jobs = newFilesToUpload.map((file, idx) =>
          queue.add(async (): Promise<FilesToAddItem | null> => {
            const upload = await api.getUploadUrl(ticketId, file.name, file.size);
            const url = upload.data;
            const response = await uploadHelper.uploadFile(
              url.uploadUrl,
              file,
              (uploadedBytes, totalBytes) => {
                const percent = totalBytes ? (uploadedBytes / totalBytes) * 100 : 0;
                setUploadProgress((prev) => ({ ...prev, [idx]: percent }));
              }
            );
            setUploadProgress((prev) => ({ ...prev, [idx]: 100 }));
            const data = response?.data;
            // A channel-scoped upload comes back with a drive id and no group, so requiring the group
            // here would discard the file as a failed upload.
            if (data?.id && (url?.groupId || url?.driveId)) {
              return {
                FileName: file.name,
                MediaType: file.type || "application/octet-stream",
                FileSize: file.size,
                DriveItemId: data.id,
                ...attachmentLocation(url),
              };
            }
            return null;
          })
        );
        const results = await Promise.all(jobs);
        filesToAdd = results.filter((r): r is FilesToAddItem => r != null);
        if (filesToAdd.length < newFilesToUpload.length) {
          throw new Error("One or more file uploads failed. Please try again.");
        }
      } finally {
        setIsUploading(false);
        setUploadProgress({});
      }
    }

    const response = await updateChatContent(comment.Id, message, isPublic, filesToAdd, filesToDelete, notify);

    setCommentBody(message);
    setCommentIsPublic(isPublic);
    if (response?.FileAttachments) {
      setCommentFileAttachments(response.FileAttachments);
      setEditedAttachmentsList(response.FileAttachments);
    }
    onCommentUpdated?.();
    setIsEditing(false);

    const editedIds = new Set(editedAttachmentsList.map((a) => a.Id));
    const newInResponse = (response?.FileAttachments ?? []).filter((a: any) => !editedIds.has(a.Id));
    const deletedIds = new Set(filesToDelete);
    const hasAttachmentChanges = newInResponse.length > 0 || deletedIds.size > 0;

    if (hasAttachmentChanges) {
      setAppState((state) => {
        let uploadedFiles = state.uploadingState?.uploadedFiles ?? [];
        if (deletedIds.size > 0) {
          uploadedFiles = uploadedFiles.filter((f) => !deletedIds.has(f.Id ?? 0));
        }
        if (newInResponse.length > 0) {
          const mappedNew: FileProps[] = newInResponse.map((f: any) => ({
            ...f,
            DownladUrl: f.DownloadUrl ?? f.DownladUrl ?? f.webUrl ?? "",
            MoveItemFailed: false,
            isHidden: false,
            isUploading: false,
            isUploadFailed: false,
            isInProgress: false,
            progressPercent: 100,
            uploadFailedMessage: "",
          }));
          uploadedFiles = [...mappedNew, ...uploadedFiles];
        }
        return {
          ...state,
          uploadingState: { ...state.uploadingState, uploadedFiles },
        };
      });
    }
  }, [
    comment?.FileAttachments,
    comment?.Id,
    commentFileAttachments,
    editedAttachmentsList,
    message,
    isPublic,
    ticketId,
    updateChatContent,
    setAppState,
    onCommentUpdated,
    setIsEditing,
  ]);

  const handleUpdateMessage = useCallback(async (newFilesToUpload: File[]) => {
    try {
      await performUpdate(newFilesToUpload, false);
    } catch (err) {
      console.error("Failed to update comment:", err);
    }
  }, [performUpdate]);

  const handleSendMessage = useCallback(async (newFilesToUpload: File[]) => {
    try {
      await performUpdate(newFilesToUpload, true);
    } catch (err) {
      console.error("Failed to send comment:", err);
    }
  }, [performUpdate]);

  return {
    assignees,
    commentBody,
    setCommentBody,
    commentIsPublic,
    setCommentIsPublic,
    commentFileAttachments,
    setCommentFileAttachments,
    attachmentsModified,
    isEditing,
    setIsEditing,
    isEditModeReady,
    message,
    setMessage,
    editedAttachmentsList,
    setEditedAttachmentsList,
    isPublic,
    setIsPublic,
    cancelEdit,
    handleUpdateMessage,
    handleSendMessage,
    updateLoading,
    actionStatus: updateLoading || isUploading ? pendingAction : undefined,
    isUploading,
    uploadProgress,
    hasChanges,
  };
};
